import { Suspense } from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';
import { Flags } from '@setvi/shared/enums';
import { ROUTES } from 'enumsV2';
import App from 'App';
import RouterModal from 'Providers/RouterModal';

import { GuestLayout, MainLayout, SetviGuestLayout } from 'Layouts';
import ProtectedProducts from 'pages/products/components/protected-products';
import { NonAuthorizedRoutes } from './non-authorized';
import {
  AuthorizedRoutes,
  FeatureFlagRoute,
  ModuleAuthorizedRoutes
} from './authorized';

// Lazy loading
import * as LazyPage from './lazy-loading/pages';
import { LazyLoader } from './lazy-loading/loader';
import { ErrorPage } from './error-page';

const ErrorTestComponent = (): JSX.Element => {
  throw new Error('Test error to demonstrate error page');
  return null;
};

const lazyLoadElement = (component: JSX.Element) => (
  <Suspense fallback={<LazyLoader />}>{component}</Suspense>
);

const routes = createBrowserRouter([
  {
    element: <App />,
    errorElement: (
      <ErrorPage
        src="/images/error-page.svg"
        title="An Unexpected Error Occurred"
        description="We apologize for the interruption. Please reload the page and try again. If the issue persists, please contact our support team for assistance."
      />
    ),
    children: [
      {
        element: <AuthorizedRoutes />,
        children: [
          {
            element: <MainLayout />,
            children: [
              {
                errorElement: (
                  <ErrorPage
                    src="/images/error-page.svg"
                    title="An Unexpected Error Occurred"
                    description="We apologize for the interruption. Please return to the previous page and try again. If the issue persists, please contact our support team for assistance."
                  />
                ),
                children: [
                  {
                    path: ROUTES.HOME,
                    element: lazyLoadElement(<LazyPage.Home />)
                  },
                  {
                    path: '/test-error',
                    element: lazyLoadElement(<ErrorTestComponent />)
                  },
                  {
                    path: ROUTES.CAMPAIGNS,
                    children: [
                      {
                        path: ROUTES.MY_CAMPAIGNS,
                        element: (
                          <FeatureFlagRoute flag={Flags.EMAIL_CAMPAIGNS}>
                            {lazyLoadElement(<LazyPage.MyCampaignsPage />)}
                          </FeatureFlagRoute>
                        )
                      },
                      {
                        path: ROUTES.MY_CAMPAIGNS_CREATE,
                        element: (
                          <FeatureFlagRoute flag={Flags.EMAIL_CAMPAIGNS}>
                            {lazyLoadElement(<LazyPage.CreateCampaign />)}
                          </FeatureFlagRoute>
                        )
                      },
                      {
                        path: ROUTES.MY_CAMPAIGNS_DETAIL,
                        element: (
                          <FeatureFlagRoute flag={Flags.EMAIL_CAMPAIGNS}>
                            {lazyLoadElement(<LazyPage.Details />)}
                          </FeatureFlagRoute>
                        )
                      },
                      {
                        path: ROUTES.MY_CAMPAIGNS_DETAIL_FOLLOW_UP,
                        element: (
                          <FeatureFlagRoute flag={Flags.EMAIL_CAMPAIGNS}>
                            {lazyLoadElement(<LazyPage.FollowUp />)}
                          </FeatureFlagRoute>
                        )
                      },
                      // COMPANY CAMPAIGNS //
                      {
                        path: ROUTES.COMPANY_CAMPAIGNS,
                        element: (
                          <FeatureFlagRoute flag={Flags.COMPANY_CAMPAIGNS}>
                            {lazyLoadElement(<LazyPage.CompanyCampaign />)}
                          </FeatureFlagRoute>
                        )
                      }
                    ]
                  },
                  {
                    path: ROUTES.SALES,
                    element: <Outlet />,
                    children: [
                      {
                        path: ROUTES.QUOTES,
                        element: (
                          <FeatureFlagRoute
                            flag={[Flags.QUOTES, Flags.QUOTES_V2]}>
                            <Outlet />
                          </FeatureFlagRoute>
                        ),
                        children: [
                          {
                            index: true,
                            element: lazyLoadElement(<LazyPage.QuoteList />)
                          },
                          {
                            path: ROUTES.QUOTE_DETAILS,
                            element: lazyLoadElement(<LazyPage.QuoteDetails />)
                          },
                          {
                            path: ROUTES.QUOTE_DETAILS_REVIEW,
                            element: lazyLoadElement(<LazyPage.QuoteReview />)
                          }
                        ]
                      },
                      {
                        path: ROUTES.AUTO_RFQ,
                        element: (
                          <FeatureFlagRoute flag={Flags.AUTO_RFQ}>
                            <Outlet />
                          </FeatureFlagRoute>
                        ),
                        children: [
                          {
                            index: true,
                            element: lazyLoadElement(<LazyPage.AutoRFQ />)
                          },
                          {
                            path: ROUTES.AUTO_RFQ_DETAILS,
                            element: lazyLoadElement(
                              <LazyPage.AutoRFQDetails />
                            )
                          }
                        ]
                      },
                      {
                        path: ROUTES.ORDERS,
                        element: (
                          <FeatureFlagRoute
                            flag={[Flags.ORDERS, Flags.ORDERS_V2]}>
                            {lazyLoadElement(<LazyPage.OrderEnablement />)}
                          </FeatureFlagRoute>
                        ),
                        children: [
                          {
                            index: true,
                            element: lazyLoadElement(<LazyPage.Orders />)
                          },
                          {
                            path: ROUTES.ORDERS_THREE_PM_REPORT,
                            element: (
                              <FeatureFlagRoute
                                flag={Flags.ORDERS_THREE_PM_REPORT}>
                                {lazyLoadElement(<LazyPage.ThreePMReport />)}
                              </FeatureFlagRoute>
                            )
                          },
                          {
                            path: ROUTES.ORDERS_BACKORDERS,
                            element: (
                              <FeatureFlagRoute flag={Flags.BACKORDERS}>
                                {lazyLoadElement(<LazyPage.Backorders />)}
                              </FeatureFlagRoute>
                            )
                          },
                          {
                            path: ROUTES.ORDERS_INVOICES,
                            element: lazyLoadElement(<LazyPage.Invoices />)
                          },
                          {
                            path: ROUTES.REORDER_INTELIGANCE,
                            element: (
                              <FeatureFlagRoute flag={Flags.ORDERS_REORDER}>
                                {lazyLoadElement(
                                  <LazyPage.RetentionIntelligenceModule />
                                )}
                              </FeatureFlagRoute>
                            )
                          },
                          {
                            path: ROUTES.REORDER_INTELIGANCE_DETAILS,
                            element: (
                              <FeatureFlagRoute flag={Flags.ORDERS_REORDER}>
                                {lazyLoadElement(
                                  <LazyPage.RetentionIntelligenceCustomerDetails />
                                )}
                              </FeatureFlagRoute>
                            )
                          }
                        ]
                      },
                      {
                        path: ROUTES.ORDER_DETAILS,
                        element: lazyLoadElement(<LazyPage.OrderDetails />)
                      },
                      {
                        path: ROUTES.ORDERS_CREATE,
                        element: lazyLoadElement(<LazyPage.OrderCreate />)
                      },
                      {
                        path: ROUTES.ORDERS_EMBEDDED_CREATE,
                        element: lazyLoadElement(
                          <LazyPage.OrderEmbeddedCreate />
                        )
                      },
                      {
                        path: ROUTES.CUSTOMER_BACKORDER_LIST,
                        element: lazyLoadElement(<LazyPage.CustomerOrders />)
                      },
                      {
                        path: ROUTES.BACKORDERS_CUSTOMER_ORDER_ITEMS_LIST,
                        element: lazyLoadElement(
                          <LazyPage.CustomerOrderItems />
                        )
                      },
                      {
                        path: ROUTES.INVOICES,
                        element: (
                          <FeatureFlagRoute flag={Flags.INVOICES}>
                            {lazyLoadElement(<LazyPage.Invoices />)}
                          </FeatureFlagRoute>
                        )
                      },
                      {
                        path: ROUTES.INVOICES_DETAILS,
                        element: (
                          <FeatureFlagRoute flag={Flags.INVOICES}>
                            {lazyLoadElement(<LazyPage.InvoiceDetails />)}
                          </FeatureFlagRoute>
                        )
                      },
                      {
                        path: ROUTES.ORDERS_INVOICES_DETAILS,
                        element: lazyLoadElement(<LazyPage.InvoiceDetails />)
                      },
                      {
                        path: ROUTES.INTELLIGENCE,
                        element: (
                          <FeatureFlagRoute
                            flag={[Flags.WHITESPACE, Flags.INTELLIGENCE_V2]}>
                            <Outlet />
                          </FeatureFlagRoute>
                        ),
                        children: [
                          {
                            index: true,
                            element: lazyLoadElement(<LazyPage.Intelligence />)
                          },
                          {
                            path: ROUTES.INTELLIGENCE_DETAILS,
                            element: lazyLoadElement(
                              <LazyPage.IntelligenceDetails />
                            )
                          }
                        ]
                      },
                      {
                        path: ROUTES.RETENTION_INTELIGANCE,
                        element: (
                          <FeatureFlagRoute flag={Flags.ORDERS_REORDER}>
                            {lazyLoadElement(
                              <LazyPage.RetentionIntelligenceModule />
                            )}
                          </FeatureFlagRoute>
                        )
                      },
                      {
                        path: ROUTES.RETENTION_INTELIGANCE_DETAILS,
                        element: (
                          <FeatureFlagRoute flag={Flags.ORDERS_REORDER}>
                            {lazyLoadElement(
                              <LazyPage.RetentionIntelligenceCustomerDetails />
                            )}
                          </FeatureFlagRoute>
                        )
                      },
                      {
                        path: ROUTES.WHITESPACE_INTELLIGENCE,
                        element: (
                          <FeatureFlagRoute
                            flag={Flags.WHITESPACE_INTELLIGENCE}>
                            {lazyLoadElement(
                              <LazyPage.WhitespaceIntelligence />
                            )}
                          </FeatureFlagRoute>
                        )
                      }
                    ]
                  },
                  {
                    path: ROUTES.RESOURCES,
                    element: (
                      <FeatureFlagRoute flag={Flags.RESOURCES}>
                        {lazyLoadElement(<LazyPage.Resources />)}
                      </FeatureFlagRoute>
                    )
                  },
                  {
                    path: ROUTES.FAVORITES,
                    element: lazyLoadElement(<LazyPage.Favorites />)
                  },
                  {
                    path: ROUTES.WORKSPACES,
                    element: (
                      <FeatureFlagRoute flag={Flags.WORKSPACES}>
                        <Outlet />
                      </FeatureFlagRoute>
                    ),
                    children: [
                      {
                        element: (
                          <ModuleAuthorizedRoutes module="MyResourcesAllowed" />
                        ),
                        children: [
                          {
                            path: ROUTES.WORKSPACES_FILES,
                            element: (
                              <FeatureFlagRoute flag={Flags.MY_FILES}>
                                {lazyLoadElement(<LazyPage.MyFiles />)}
                              </FeatureFlagRoute>
                            )
                          }
                        ]
                      },
                      {
                        path: ROUTES.WORKSPACES_PRESENTATIONS,
                        element: (
                          <FeatureFlagRoute flag={Flags.MY_PRESENTATIONS}>
                            {lazyLoadElement(<LazyPage.MyPresentation />)}
                          </FeatureFlagRoute>
                        )
                      },
                      {
                        path: ROUTES.WORKSPACES_PRESENTATION_DETAILS,
                        element: (
                          <FeatureFlagRoute flag={Flags.MY_PRESENTATIONS}>
                            {lazyLoadElement(<LazyPage.PresentationDetails />)}
                          </FeatureFlagRoute>
                        )
                      },
                      {
                        path: ROUTES.WORKSPACES_TEMPLATE_DETAILS,
                        element: (
                          <FeatureFlagRoute flag={Flags.MY_PRESENTATIONS}>
                            {lazyLoadElement(<LazyPage.TemplateDetails />)}
                          </FeatureFlagRoute>
                        )
                      },
                      {
                        path: ROUTES.WORKSPACES_SNIPPETS,
                        element: (
                          <FeatureFlagRoute flag={Flags.SNIPPETS}>
                            {lazyLoadElement(<LazyPage.WorkspacesSnippets />)}
                          </FeatureFlagRoute>
                        )
                      },
                      {
                        path: ROUTES.WORKSPACES_CONTACTS_GROUPS,
                        element: (
                          <FeatureFlagRoute flag={Flags.CONTACTS_GROUPS}>
                            {lazyLoadElement(<LazyPage.ContactsAndGroups />)}
                          </FeatureFlagRoute>
                        ),
                        children: [
                          {
                            index: true,
                            element: lazyLoadElement(<LazyPage.Contacts />)
                          },
                          {
                            path: ROUTES.WORKSPACES_GROUPS,
                            element: lazyLoadElement(<LazyPage.Groups />)
                          }
                        ]
                      },
                      {
                        path: ROUTES.WORKSPACES_GROUPS_DETAIL,
                        element: lazyLoadElement(<LazyPage.GroupDetail />)
                      },
                      {
                        path: ROUTES.WORKSPACES_DIGITAL_ROOMS,
                        element: lazyLoadElement(<LazyPage.DigitalRooms />)
                      }
                    ]
                  },
                  {
                    path: ROUTES.EMAIL_QUEUE,
                    element: lazyLoadElement(<LazyPage.EmailQueue />)
                  },
                  {
                    path: ROUTES.OUTBOX,
                    element: (
                      <FeatureFlagRoute flag={Flags.OUTBOX}>
                        {lazyLoadElement(<LazyPage.Outbox />)}
                      </FeatureFlagRoute>
                    ),
                    children: [
                      {
                        index: true,
                        element: lazyLoadElement(<LazyPage.Emails />)
                      },
                      {
                        path: ROUTES.OUTBOX_GET_LINK,
                        element: lazyLoadElement(<LazyPage.OutboxGetLink />)
                      },
                      {
                        path: ROUTES.OUTBOX_OUTLOOK,
                        element: (
                          <FeatureFlagRoute flag={Flags.OUTBOX_OUTLOOK}>
                            {lazyLoadElement(<LazyPage.Outlook />)}
                          </FeatureFlagRoute>
                        )
                      },
                      {
                        path: ROUTES.OUTBOX_CAMPAIGN,
                        element: lazyLoadElement(<LazyPage.OutboxMyCampaigns />)
                      },
                      {
                        path: ROUTES.OUTBOX_QUOTES,
                        element: lazyLoadElement(<LazyPage.OutboxQuoteShares />)
                      }
                    ]
                  },
                  {
                    path: ROUTES.OUTBOX_GET_LINK_DETAILS,
                    element: lazyLoadElement(<LazyPage.GetLinkDetails />)
                  },
                  {
                    path: ROUTES.OUTBOX_OUTLOOK_DETAILS,
                    element: lazyLoadElement(<LazyPage.OutlookDetails />),
                    children: [
                      {
                        index: true,
                        element: lazyLoadElement(
                          <LazyPage.OutlookEmailDetails />
                        )
                      },
                      {
                        path: ROUTES.OUTBOX_OUTLOOK_VIEWS,
                        element: lazyLoadElement(<LazyPage.OutlookViews />)
                      }
                    ]
                  },
                  {
                    path: ROUTES.OUTBOX_EMAIL_DETAILS_MULTIPLE,
                    element: lazyLoadElement(
                      <LazyPage.MultipleRecipientEmailDetails />
                    ),
                    children: [
                      {
                        path: ROUTES.OUTBOX_EMAIL_DETAILS_MULTIPLE_OVERVIEW,
                        element: lazyLoadElement(<LazyPage.Overview />)
                      },
                      {
                        path: ROUTES.OUTBOX_EMAIL_DETAILS_MULTIPLE_EMAIL_DETAILS,
                        element: lazyLoadElement(<LazyPage.EmailDetails />)
                      }
                    ]
                  },
                  {
                    path: ROUTES.OUTBOX_EMAIL_DETAILS_SINGLE,
                    element: lazyLoadElement(
                      <LazyPage.SingleRecipientEmailDetails />
                    )
                  },
                  {
                    path: ROUTES.DIGITAL_SHOWROOM_TELABIO_ONE,
                    element: (
                      <FeatureFlagRoute
                        flag={Flags.DIGITAL_SHOWROOM_TELA_BIO_ONE}>
                        {lazyLoadElement(
                          <LazyPage.TelaBioEmbed url="dpf_list" />
                        )}
                      </FeatureFlagRoute>
                    )
                  },
                  // {/* ******* REVENUE MANAGEMENT TelaBio ******** */}
                  {
                    path: ROUTES.DP_FORM,
                    element: (
                      <FeatureFlagRoute
                        flag={Flags.DIGITAL_SHOWROOM_TELA_BIO_ONE}>
                        <LazyPage.TelaBioEmbed url="dpf_list" />
                      </FeatureFlagRoute>
                    )
                  },
                  {
                    path: ROUTES.INVENTORY,
                    element: (
                      <FeatureFlagRoute
                        flag={Flags.DIGITAL_SHOWROOM_TELA_BIO_ONE}>
                        <LazyPage.TelaBioEmbed url="inventory" />
                      </FeatureFlagRoute>
                    )
                  },
                  {
                    path: ROUTES.CUSTOMERS,
                    element: (
                      <FeatureFlagRoute
                        flag={Flags.DIGITAL_SHOWROOM_TELA_BIO_ONE}>
                        <LazyPage.TelaBioEmbed url="customers" />
                      </FeatureFlagRoute>
                    )
                  },
                  {
                    path: ROUTES.CONSIGNMENT,
                    element: (
                      <FeatureFlagRoute
                        flag={Flags.DIGITAL_SHOWROOM_TELA_BIO_ONE}>
                        <LazyPage.TelaBioEmbed url="consignment" />
                      </FeatureFlagRoute>
                    )
                  },
                  {
                    path: ROUTES.INVENTORY_CHECKS,
                    element: (
                      <FeatureFlagRoute
                        flag={Flags.DIGITAL_SHOWROOM_TELA_BIO_ONE}>
                        <LazyPage.TelaBioEmbed url="inventoryChecks" />
                      </FeatureFlagRoute>
                    )
                  },
                  // {/* ******* REPORTING ******** */}
                  {
                    path: ROUTES.EVENTS,
                    element: (
                      <FeatureFlagRoute flag={Flags.TELA_BIO_EVENTS}>
                        <LazyPage.TelaBioEmbed url="eventList" />
                      </FeatureFlagRoute>
                    )
                  },
                  {
                    path: ROUTES.THOUGHTSPOT_LIVEBOARD_DETAILS,
                    element: (
                      <FeatureFlagRoute flag={Flags.THOUGHTSPOT_LIVEBOARDS}>
                        <LazyPage.ThoughtspotLiveboard />
                      </FeatureFlagRoute>
                    )
                  },
                  // {/* ******* ADMIN ******** */}
                  {
                    path: ROUTES.ADMIN,
                    children: [
                      {
                        path: ROUTES.ADMIN_CAMPAIGNS,
                        element: (
                          <FeatureFlagRoute flag={Flags.ADMIN_COMPANY_CAMPAIGN}>
                            {lazyLoadElement(<LazyPage.AdminCampaigns />)}
                          </FeatureFlagRoute>
                        ),
                        children: [
                          {
                            index: true,
                            element: lazyLoadElement(
                              <LazyPage.AdminCampaignsTable />
                            )
                          },
                          {
                            path: ROUTES.ADMIN_CAMPAIGNS_DETAILS,
                            element: lazyLoadElement(
                              <LazyPage.AdminCampaignDetails />
                            ),
                            children: [
                              {
                                index: true,
                                element: lazyLoadElement(
                                  <LazyPage.AdminCampaignDetailsContent />
                                )
                              },
                              {
                                path: ROUTES.ADMIN_CAMPAIGNS_DETAILS_RECIPIENTS,
                                element: lazyLoadElement(
                                  <LazyPage.AdminCampaignDetailsRecipients />
                                )
                              }
                            ]
                          },
                          {
                            path: ROUTES.ADMIN_CAMPAIGNS_DETAILS_USER_CAMPAIGNS,
                            element: lazyLoadElement(<LazyPage.Details />)
                          },
                          {
                            path: ROUTES.ADMIN_CAMPAIGNS_CREATE,
                            element: lazyLoadElement(
                              <LazyPage.CreateAdminCampaign />
                            )
                          },
                          {
                            path: ROUTES.ADMIN_CAMPAIGNS_EDIT,
                            element: lazyLoadElement(
                              <LazyPage.UpdateAdminCampaign />
                            )
                          }
                        ]
                      },
                      {
                        path: ROUTES.ADMIN_RESOURCES,
                        element: (
                          <FeatureFlagRoute flag={Flags.ADMIN_RESOURCES}>
                            {lazyLoadElement(<LazyPage.AdminResources />)}
                          </FeatureFlagRoute>
                        ),
                        children: [
                          {
                            path: ROUTES.ADMIN_RESOURCES_CREATE_CATEGORY,
                            element: (
                              <RouterModal
                                route={ROUTES.ADMIN_RESOURCES_CREATE_CATEGORY}
                                fallbackRoute={ROUTES.ADMIN_RESOURCES}>
                                {lazyLoadElement(<LazyPage.CreateCategory />)}
                              </RouterModal>
                            )
                          },
                          {
                            path: ROUTES.ADMIN_RESOURCES_EDIT_CATEGORY,
                            element: (
                              <RouterModal
                                route={ROUTES.ADMIN_RESOURCES_EDIT_CATEGORY}
                                fallbackRoute={ROUTES.ADMIN_RESOURCES}>
                                {lazyLoadElement(<LazyPage.EditCategory />)}
                              </RouterModal>
                            )
                          },
                          {
                            path: ROUTES.ADMIN_RESOURCES_ADD_SINGLE,
                            element: (
                              <RouterModal
                                route={ROUTES.ADMIN_RESOURCES_ADD_SINGLE}
                                fallbackRoute={ROUTES.ADMIN_RESOURCES}>
                                {lazyLoadElement(
                                  <LazyPage.AddSingleResource />
                                )}
                              </RouterModal>
                            )
                          },
                          {
                            path: ROUTES.ADMIN_RESOURCES_EDIT_SINGLE,
                            element: (
                              <RouterModal
                                route={ROUTES.ADMIN_RESOURCES_EDIT_SINGLE}
                                fallbackRoute={ROUTES.ADMIN_RESOURCES}>
                                {lazyLoadElement(
                                  <LazyPage.EditSingleResource />
                                )}
                              </RouterModal>
                            )
                          },
                          {
                            path: ROUTES.ADMIN_RESOURCES_ADD_BULK,
                            element: (
                              <RouterModal
                                route={ROUTES.ADMIN_RESOURCES_ADD_BULK}
                                fallbackRoute={ROUTES.ADMIN_RESOURCES}>
                                {lazyLoadElement(<LazyPage.AddBulkResource />)}
                              </RouterModal>
                            )
                          },
                          {
                            path: ROUTES.ADMIN_RESOURCES_ADD_ZIP,
                            element: (
                              <RouterModal
                                route={ROUTES.ADMIN_RESOURCES_ADD_ZIP}
                                fallbackRoute={ROUTES.ADMIN_RESOURCES}>
                                {lazyLoadElement(<LazyPage.AddZipResource />)}
                              </RouterModal>
                            )
                          }
                        ]
                      },
                      {
                        path: ROUTES.ADMIN_USERS,
                        element: (
                          <FeatureFlagRoute flag={Flags.ADMIN_USERS}>
                            {lazyLoadElement(<LazyPage.AdminUsers />)}
                          </FeatureFlagRoute>
                        ),
                        children: [
                          {
                            path: ROUTES.ADMIN_USERS,
                            element: lazyLoadElement(
                              <LazyPage.AdminUsersHome />
                            ),
                            children: [
                              {
                                path: ROUTES.ADMIN_USERS_CREATE,
                                element: (
                                  <RouterModal
                                    route={ROUTES.ADMIN_USERS_CREATE}
                                    fallbackRoute={ROUTES.ADMIN_USERS}>
                                    {lazyLoadElement(
                                      <LazyPage.AdminUserCreate />
                                    )}
                                  </RouterModal>
                                )
                              },
                              {
                                path: ROUTES.ADMIN_USERS_EDIT,
                                element: (
                                  <RouterModal
                                    route={ROUTES.ADMIN_USERS_EDIT}
                                    fallbackRoute={ROUTES.ADMIN_USERS}>
                                    {lazyLoadElement(
                                      <LazyPage.AdminUserEdit />
                                    )}
                                  </RouterModal>
                                )
                              }
                            ]
                          }
                        ]
                      },
                      {
                        path: ROUTES.HOME_SCREEN,
                        element: (
                          <FeatureFlagRoute flag={Flags.ADMIN_HOME_SCREEN}>
                            {lazyLoadElement(<LazyPage.HomeScreen />)}
                          </FeatureFlagRoute>
                        ),
                        children: [
                          {
                            index: true,
                            element: lazyLoadElement(
                              <LazyPage.ManageHomeScreen />
                            )
                          },
                          {
                            path: ROUTES.ADD_BANNER,
                            element: lazyLoadElement(<LazyPage.AddBanner />)
                          },
                          {
                            path: ROUTES.EDIT_BANNER,
                            element: lazyLoadElement(<LazyPage.EditBanner />)
                          },
                          {
                            path: ROUTES.ADD_FEATURED_RESOURCE,
                            element: lazyLoadElement(
                              <LazyPage.AddFeaturedResource />
                            )
                          },
                          {
                            path: ROUTES.EDIT_FEATURED_RESOURCE,
                            element: lazyLoadElement(
                              <LazyPage.EditFeaturedResource />
                            )
                          }
                        ]
                      },
                      {
                        path: ROUTES.ADMIN_AI_SALES_ENABLEMENT,
                        element: (
                          <FeatureFlagRoute flag={Flags.SALES_ENABLEMENT}>
                            {lazyLoadElement(
                              <LazyPage.AdminAISalesEnablement />
                            )}
                          </FeatureFlagRoute>
                        )
                      }
                    ]
                  },
                  {
                    path: ROUTES.SETTINGS,
                    element: lazyLoadElement(<LazyPage.Settings />)
                  },
                  {
                    element: (
                      <ErrorPage
                        src="/images/404-page.svg"
                        title="Oops! Looks like you got lost"
                        description="Let’s get you back home by clicking the below button."
                      />
                    ),
                    path: ROUTES.NOT_FOUND
                  }
                ]
              },
              {
                path: ROUTES.PRODUCTS,
                element: (
                  <ProtectedProducts>
                    {lazyLoadElement(<LazyPage.Products />)}
                  </ProtectedProducts>
                )
              },
              {
                path: ROUTES.PRODUCTS_CUSTOMER_VIEW,
                element: (
                  <ProtectedProducts>
                    {lazyLoadElement(<LazyPage.CustomerViewPage />)}
                  </ProtectedProducts>
                )
              },
              // Do NOT remove — this route is used externally as a standalone product view
              {
                path: ROUTES.PRODUCT_DETAILS,
                element: (
                  <ProtectedProducts>
                    {lazyLoadElement(<LazyPage.ProductDetailsPage />)}
                  </ProtectedProducts>
                )
              },
              {
                path: ROUTES.OUTLOOK_CATALOG,
                element: (
                  <ProtectedProducts>
                    {lazyLoadElement(<LazyPage.OutlookCatalog />)}
                  </ProtectedProducts>
                )
              },
              {
                path: ROUTES.SETTINGS,
                element: lazyLoadElement(<LazyPage.Settings />)
              }
            ]
          },
          // Standalone embed routes — authorized but without MainLayout chrome,
          // intended to be iframed from a host app.
          {
            path: ROUTES.CUSTOMER_PRODUCTS_EMBED,
            element: lazyLoadElement(<LazyPage.CustomerProductsEmbed />)
          },
          {
            path: ROUTES.WHITESPACE_CATEGORIES_EMBED,
            element: lazyLoadElement(<LazyPage.WhitespaceCategoriesEmbed />)
          }
        ]
      },
      {
        element: <NonAuthorizedRoutes />,
        children: [
          {
            element: window.location.origin.includes('middleby') ? (
              <GuestLayout />
            ) : (
              <SetviGuestLayout />
            ),
            children: [
              {
                path: ROUTES.LOGIN,
                element: lazyLoadElement(<LazyPage.Authenticate />)
              }
            ]
          }
        ]
      },
      {
        element: window.location.origin.includes('middleby') ? (
          <GuestLayout />
        ) : (
          <SetviGuestLayout />
        ),
        children: [
          {
            path: ROUTES.VERIFY_EMAIL,
            element: lazyLoadElement(<LazyPage.EmailVerification />)
          }
        ]
      },
      {
        element: window.location.origin.includes('middleby') ? (
          <GuestLayout />
        ) : (
          <SetviGuestLayout />
        ),
        children: [
          {
            path: ROUTES.SSO_CALLBACK,
            element: lazyLoadElement(<LazyPage.SSOCallback />)
          }
        ]
      }
    ]
  }
]);

export const AppRoutes = () => (
  <RouterProvider future={{ v7_startTransition: true }} router={routes} />
);
